/**
 * Утилиты для озвучки текста через ElevenLabs API.
 * Озвучка идёт ТОЛЬКО через прокси /api/tts на бэкенде — ключ ElevenLabs
 * хранится исключительно на сервере и никогда не попадает в браузер/бандл.
 * Если прокси недоступен — фолбэк на Web Speech API.
 */

import api from './api';

const API_URL = process.env.REACT_APP_API_URL || '';

export type TtsDebugInfo = {
  provider: 'backend-proxy' | 'web-speech';
  voiceId?: string;
};

const stripMarkdownForTts = (input: string): string => {
  // Убираем наиболее частые маркеры разметки, которые TTS читает вслух (*, _, `, ссылки).
  // Сохраняем смысл текста, не пытаясь полноценно парсить Markdown.
  return input
    .replace(/\r\n/g, '\n')
    // [текст](ссылка) -> текст
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    // **жирный** / *курсив* / __ / _
    .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, '$1')
    // одиночные оставшиеся звёздочки/подчёркивания
    .replace(/[*_]+/g, ' ')
    // инлайн-код `code` -> code
    .replace(/`([^`]+)`/g, '$1')
    // лишние пробелы
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Генерирует аудио из текста: бэкенд-прокси /api/tts (ElevenLabs на сервере),
 * при недоступности — фолбэк на Web Speech API.
 */
export const generateAudio = async (text: string): Promise<string> => {
  const raw = (typeof text === 'string' ? text : '').trim();
  const t = stripMarkdownForTts(raw);
  if (!t) return '';

  // Прокси через бэкенд — ключ только на сервере, без CORS
  if (API_URL) {
    try {
      const res = await api.post<Blob>('/tts', { text: t }, { responseType: 'blob' });
      const blob = res.data;
      if (blob && blob.size > 0) {
        return URL.createObjectURL(blob);
      }
    } catch (err) {
      console.warn('TTS proxy failed, falling back to Web Speech:', err);
    }
  }

  // Fallback: Web Speech API
  return playWithWebSpeech(t);
};

export const getTtsDebugInfo = (): TtsDebugInfo => {
  if (API_URL) return { provider: 'backend-proxy' };
  return { provider: 'web-speech' };
};

/**
 * Выбирает наиболее подходящий голос для русского (меньше «робота»)
 */
const selectRussianVoice = (): SpeechSynthesisVoice | null => {
  const voices = window.speechSynthesis.getVoices();
  const ru = voices.filter((v) => v.lang.startsWith('ru'));
  // Предпочитаем голоса с названием, где есть Google/Microsoft/Yandex — часто качественнее
  const preferred = ru.find((v) => /Google|Microsoft|Yandex|Samantha|Milena|Katya/i.test(v.name));
  return preferred || ru[0] || null;
};

/**
 * Воспроизводит текст используя Web Speech API (fallback, когда нет ElevenLabs)
 * Звучит заметно менее естественно, чем ElevenLabs — см. docs/API_KEYS_SETUP.md
 */
const playWithWebSpeech = (text: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!('speechSynthesis' in window)) {
      reject(new Error('Speech synthesis not supported'));
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'ru-RU';
    utterance.rate = 0.85;
    utterance.pitch = 1.02;
    utterance.volume = 1;
    const voice = selectRussianVoice();
    if (voice) utterance.voice = voice;

    utterance.onend = () => resolve('');
    utterance.onerror = (error) => reject(error);

    window.speechSynthesis.speak(utterance);
  });
};

// Инициализация списка голосов в Chrome (загружается асинхронно)
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  window.speechSynthesis.getVoices();
  window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
}

// Короткий «тихий» WAV для разблокировки автозапуска (Safari/iOS/WebKit).
const SILENT_AUDIO =
  'data:audio/wav;base64,UklGRsQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YaAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA';

// Один переиспользуемый <audio>: после успешного play() по клику он остаётся
// «разблокированным», и последующий play() (даже после await) уже разрешён.
let sharedAudio: HTMLAudioElement | null = null;

const getSharedAudio = (): HTMLAudioElement => {
  if (!sharedAudio) sharedAudio = new Audio();
  return sharedAudio;
};

/**
 * Разблокирует воспроизведение аудио. ВАЖНО: вызывать СИНХРОННО внутри
 * обработчика клика (до любых await), иначе Safari/WebKit заблокирует звук
 * (NotAllowedError), т.к. потеряется контекст пользовательского жеста.
 */
export const primeAudioPlayback = (): void => {
  try {
    const audio = getSharedAudio();
    audio.src = SILENT_AUDIO;
    const p = audio.play();
    if (p && typeof (p as Promise<void>).then === 'function') {
      (p as Promise<void>).catch(() => {});
    }
  } catch {
    // игнорируем — в худшем случае сработает обычный путь воспроизведения
  }
};

/**
 * Воспроизводит аудио из URL через разблокированный элемент.
 */
export const playAudio = (audioUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const audio = getSharedAudio();
    audio.src = audioUrl;
    audio.onended = () => resolve();
    audio.onerror = (error) => reject(error);
    const p = audio.play();
    if (p && typeof (p as Promise<void>).then === 'function') {
      (p as Promise<void>).catch(reject);
    }
  });
};
